import { intentAttachmentsApi } from '@/api/intentAttachments'
import type { PaginationParams } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const attachmentsKey = (projectId: string, intentId: string, params?: Record<string, unknown>) =>
    params
        ? ['projects', projectId, 'intents', intentId, 'attachments', params] as const
        : ['projects', projectId, 'intents', intentId, 'attachments'] as const

export function useIntentAttachments(projectId: string, intentId: string, pagination?: PaginationParams) {
    const params = pagination ? { ...pagination } : undefined

    return useQuery({
        queryKey: attachmentsKey(projectId, intentId, params),
        queryFn: () => intentAttachmentsApi.getByIntent(projectId, intentId, params),
        enabled: !!projectId && !!intentId,
        staleTime: 0,
    })
}

export function useUploadIntentAttachments(projectId: string, intentId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (files: File[]) => intentAttachmentsApi.upload(projectId, intentId, files),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: attachmentsKey(projectId, intentId),
            })
        },
    })
}

export function useDeleteIntentAttachment(projectId: string, intentId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (attachmentId: string) =>
            intentAttachmentsApi.delete(projectId, intentId, attachmentId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: attachmentsKey(projectId, intentId),
            })
        },
    })
}

export function useIntentAttachmentDownloadUrl(projectId: string, intentId: string) {
    return useMutation({
        mutationFn: (attachmentId: string) =>
            intentAttachmentsApi.getDownloadUrl(projectId, intentId, attachmentId),
        onSuccess: (url) => {
            window.open(url, '_blank', 'noopener,noreferrer')
        },
    })
}
